/**
 * A1 · Sign in — an email, its password, the product's name, nothing about the fund.
 *
 * Passwords are made on the server (`biandang-access add`), shown once there, and never chosen
 * here; `autocomplete` lets the browser's password manager keep the random one.
 *
 * **Every 401 body is a state**, not an error, and its sentence sits above the form, verbatim:
 *
 *   rejected              *"That email and password don't match."* — the same for an address that
 *                         isn't on the list, so the door doesn't say who is
 *   session ended         the server's sentence, which says the fund did NOT stop: *"Nothing on the
 *                         server stopped: the watcher and the funds kept running."* Whoever is
 *                         bounced here at 11:20 needs that before anything else, and the console does
 *                         not paraphrase it. A password reset since sign-in ends a session the same way.
 *   removed from the list  the sentence; the form stays, because someone else may be at this browser
 *   sign-in failed        no answer came back at all, which is the browser's sentence, not the server's
 */

import { type FormEvent, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Failed } from "../api/client";
import { signIn } from "../api/auth";
import { useSession } from "../store/session";
import { useT } from "../i18n/useT";
import { Logo } from "../components/ui/Logo";
import { Toggles } from "../components/competition/Header";

export function SignIn() {
  const { t } = useT();
  const who = useSession((s) => s.who);
  const state = useSession((s) => s.state);
  const says = useSession((s) => s.says);
  const check = useSession((s) => s.check);
  const verifying = useSession((s) => s.verifying);
  const failed = useSession((s) => s.failed);
  const signedIn = useSession((s) => s.signedIn);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!useSession.getState().checked) void check();
  }, [check]);

  if (who) return <Navigate to="/" replace />;

  const said = says || (state === "idle" ? t("a1.idle") : "");
  const busy = state === "verifying";

  async function submit(event: FormEvent) {
    event.preventDefault();
    verifying();
    try {
      signedIn(await signIn(email, password));
    } catch (error) {
      setPassword("");
      // The backend's own refusal body is the state and the sentence, rendered verbatim.
      if (error instanceof Failed && (error.trouble.kind === "signed out" || error.trouble.kind === "refused")) {
        useSession.getState().bounced(error.trouble.refusal.state, error.trouble.refusal.says);
      } else {
        failed(t("a1.unanswered"));
      }
    }
  }

  return (
    <div className="entry">
      <div style={{ position: "fixed", top: "var(--s3)", right: "var(--gutter)" }}>
        <Toggles />
      </div>

      <div className="entry__box" data-state={state} data-level="takeover" data-tone="neutral">
        {/* Sign-in gets logo 2, which is only drawn at 64px and up. */}
        <Logo mark="e" size={112} />
        <h1 className="entry__name">{t("a1.title")}</h1>
        <p className="entry__says">{said || t("a1.lede")}</p>

        <div className="entry__acts">
          <form className="entry__form" onSubmit={(event) => void submit(event)} aria-busy={busy}>
            <label className="field">
              <span className="lbl">{t("a1.email")}</span>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="field">
              <span className="lbl">{t("a1.password")}</span>
              <input
                type="password"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
            <button type="submit" className="btn btn--accent" disabled={busy}>
              {t("a1.submit")}
            </button>
          </form>

          {busy ? (
            <p className="sub" role="status">
              {t("a1.verifying")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
